import { IdConta, IdContato } from "../common-types";
import { GenericServiceBase } from "../generic-service";

export type LicenciadoAxion = {
    id: string;
    cpfCnpj: string;
    nome: string;
    estado?: string;
    cidade?: string;
}

export type NegociacaoFunilAxion = {
    id: string;
    nome: string;
}

/** Módulos do Axion que a chave da integração alcança (nó "Ações Axion" do bot). */
export type ModulosAxion = {
    crm: boolean;
}

/** Marcador (etiqueta de oportunidade) do CRM do Axion. */
export type MarcadorAxion = {
    id: number;
    nome: string;
    cor: string | null;
}

export type NegociacaoEtapaAxion = {
    id: string;
    nome: string;
    ordem: number;
    idFunil: string;
}

export type OportunidadeAxion = {
    id: string;
    empresa: string;
    etapa: string;
    produto: string;
    perfil: string;
}

export type LicenciadoResponsavelAxion = {
    id: string;
    nome: string;
    whatsapp: string;
}

export type UsuarioAxion = {
    id: string;
    nome: string;
    email?: string;
}

export type RespostaVinculaResponsavel = {
    idClienteAxion: string;
    idResponsavelAxion: string;
}

export type GerarOportunidade = {
    idContato: IdContato;
    nome: string;
    etapa: string;
    idResponsavel?: string;
}

export type CanalChamadoAxion = {
    id: string;
    nome: string;
}

export type ServicoChamadoAxion = {
    id: string;
    nome: string;
    prioridade: string;
}

export type AbrirChamadoAxion = {
    idAtendimento: string;
    idServico: string;
    prioridade: string;
    descricao: string;
}

export type ChamadoContatoAxion = {
    id: string | number;
    publicId?: string | null;
    status: string;
    prioridade?: string | null;
    descricao?: string | null;
    dataCadastro: string;
    servico?: string | null;
    canal?: string | null;
}

export type ChamadoAxion = {
    id: string;
    idAtendimento: string;
    idChamadoAxion: string;
    idConta: string;
    idServicoAxion: string | null;
    prioridade: string | null;
    descricao: string | null;
    status: string;
    idUsuario: string | null;
    dataCriacao: string;
}

export type OpcaoProcessoAxion = {
    id: string;
    nome: string;
}

export type MatrizProcessoAxion = {
    id: string;
    nome: string;
    idCategoria: string;
    descricao: string | null;
}

export type ResponsavelProcessoAxion = {
    id: string;
    nome: string;
    email: string | null;
}

export type OpcoesProcessoAxion = {
    categorias: OpcaoProcessoAxion[];
    matrizes: MatrizProcessoAxion[];
    projetos: OpcaoProcessoAxion[];
    solicitantes: OpcaoProcessoAxion[];
    responsaveis: ResponsavelProcessoAxion[];
}

export type DispararProcessoAxion = {
    idAtendimento: string;
    idMatriz: string;
    descricao: string;
    titulo?: string;
    idSolicitante?: string;
    idProjeto?: string;
    idResponsavelNotificar?: string;
}

export type ProcessoDisparadoAxion = {
    id: string;
    idAtendimento: string;
    idProcessoAxion: string;
    idConta: string;
    idMatrizAxion: string;
    titulo: string | null;
    idUsuario: string | null;
    dataCriacao: string;
    anexosComFalha: string[];
}

export type ProcessoContatoAxion = {
    id: string;
    titulo: string;
    status: string;
    categoria: string | null;
    matriz: string | null;
    solicitante: string | null;
    dataDisparo: string;
    etapasConcluidas: number;
    etapasTotal: number;
}

export class IntegracaoAxionService extends GenericServiceBase {

    constructor() {
        super('integracao-axion');
    }

    private readonly opts = { doNotRedirectOn401: true };

    async listarLicenciados(idConta: IdConta): Promise<LicenciadoAxion[]> {
        return this.f.get('/licenciados', { idConta }, this.opts) as unknown as LicenciadoAxion[];
    }

    async listarFunis(idConta: IdConta): Promise<NegociacaoFunilAxion[]> {
        return this.f.get('/funis', { idConta }, this.opts) as unknown as NegociacaoFunilAxion[];
    }

    async listarEtapas(idConta: IdConta): Promise<NegociacaoEtapaAxion[]> {
        return this.f.get('/etapas', { idConta }, this.opts) as unknown as NegociacaoEtapaAxion[];
    }

    async listarModulos(idConta: IdConta): Promise<ModulosAxion> {
        return this.f.get('/modulos', { idConta }, this.opts) as unknown as ModulosAxion;
    }

    async listarMarcadores(idConta: IdConta): Promise<MarcadorAxion[]> {
        return this.f.get('/marcadores', { idConta }, this.opts) as unknown as MarcadorAxion[];
    }

    async listarResponsaveis(idConta: IdConta, idLicenciado: string): Promise<LicenciadoResponsavelAxion[]> {
        return this.f.get('/listar-responsaveis', { idConta, idLicenciado }, this.opts) as unknown as LicenciadoResponsavelAxion[];
    }

    async listarUsuariosAxion(idConta: IdConta): Promise<UsuarioAxion[]> {
        return this.f.get('/listar-usuarios-axion', { idConta }, this.opts) as unknown as UsuarioAxion[];
    }

    async listarOportunidades(idConta: IdConta, idContato: string): Promise<OportunidadeAxion[]> {
        return this.f.get('/listar-oportunidades', { idConta, idContato }, this.opts) as unknown as OportunidadeAxion[];
    }

    async vincularResponsavel(idContato: IdContato, idLicenciado: string, nomeLicenciado: string, idResponsavel?: string): Promise<RespostaVinculaResponsavel | null> {
        return this.f.post('/vincular-responsavel', {
            idContato,
            idLicenciado: String(idLicenciado),
            nomeLicenciado,
            idResponsavel: idResponsavel != null ? String(idResponsavel) : undefined,
        }, this.opts) as unknown as RespostaVinculaResponsavel;
    }

    async gerarOportunidade(dto: GerarOportunidade) {
        return this.f.post('/gerar-oportunidade', dto, this.opts);
    }

    async desvincularResponsavel(idContato: IdContato): Promise<void> {
        return this.f.delete('/desvincular-responsavel', { idContato }, this.opts) as unknown as void;
    }

    async acessarOportunidade(idContato: IdContato, idOportunidade: string): Promise<{ url: string }> {
        return this.f.get('/acessar-oportunidade', { idContato, idOportunidade }, this.opts) as unknown as { url: string };
    }

    async acessarFicha(idContato: IdContato): Promise<{ url: string }> {
        return this.f.get('/acessar-ficha', { idContato }, this.opts) as unknown as { url: string };
    }

    async gerarTarefa(idContato: IdContato, idAtendimento: string): Promise<{ url: string }> {
        return this.f.get('/gerar-tarefa', { idContato, idAtendimento }, this.opts) as unknown as { url: string };
    }

    async listarCanaisChamado(idConta: IdConta): Promise<CanalChamadoAxion[]> {
        return this.f.get('/listar-canais-chamado', { idConta }, this.opts) as unknown as CanalChamadoAxion[];
    }

    async listarServicosChamado(idConta: IdConta, idCanal?: string): Promise<ServicoChamadoAxion[]> {
        return this.f.get('/listar-servicos-chamado', { idConta, idCanal }, this.opts) as unknown as ServicoChamadoAxion[];
    }

    async abrirChamado(dto: AbrirChamadoAxion, arquivos: File[] = []): Promise<ChamadoAxion> {
        return this.f.post('/abrir-chamado', { ...dto, arquivos }, { ...this.opts, formdata: true, jsonPayload: false }) as unknown as ChamadoAxion;
    }

    async temModuloChamado(idContato: IdContato): Promise<{ temModulo: boolean }> {
        return this.f.get('/chamado-modulo-disponivel', { idContato }, this.opts) as unknown as { temModulo: boolean };
    }

    async listarChamadosDoContato(idContato: IdContato): Promise<ChamadoContatoAxion[]> {
        return this.f.get('/chamados-contato', { idContato }, this.opts) as unknown as ChamadoContatoAxion[];
    }

    async listarChamadosDoAtendimento(idAtendimento: string): Promise<ChamadoAxion[]> {
        return this.f.get('/chamados-atendimento', { idAtendimento }, this.opts) as unknown as ChamadoAxion[];
    }

    async listarOpcoesProcesso(idContato: IdContato): Promise<OpcoesProcessoAxion> {
        return this.f.get('/processo-opcoes', { idContato }, this.opts) as unknown as OpcoesProcessoAxion;
    }

    async dispararProcesso(dto: DispararProcessoAxion, arquivos: File[] = []): Promise<ProcessoDisparadoAxion> {
        // Opcional vazio não pode virar campo no multipart: o servidor valida id numérico.
        const campos = Object.fromEntries(Object.entries(dto).filter(([, valor]) => valor !== undefined && valor !== ''));
        return this.f.post('/disparar-processo', { ...campos, arquivos }, { ...this.opts, formdata: true, jsonPayload: false }) as unknown as ProcessoDisparadoAxion;
    }

    async acessarProcesso(idContato: IdContato, idProcesso: string): Promise<{ url: string } | null> {
        return this.f.get('/acessar-processo', { idContato, idProcesso }, this.opts) as unknown as { url: string } | null;
    }

    async listarProcessosDoContato(idContato: IdContato): Promise<ProcessoContatoAxion[]> {
        return this.f.get('/processos-contato', { idContato }, this.opts) as unknown as ProcessoContatoAxion[];
    }
}
